<template>
  <div class="rounded-lg border border-slate-200 bg-surface text-sm text-ink">
    <button
      type="button"
      class="flex w-full items-center gap-2 px-4 py-2.5 text-left"
      :aria-expanded="expanded"
      @click="toggleExpanded"
    >
      <ChevronDownIcon
        class="h-4 w-4 shrink-0 text-ink-mute transition-transform duration-150"
        :class="expanded ? 'rotate-0' : '-rotate-90'"
      />
      <span class="font-semibold text-ink-soft">
        Billing documents
        <span class="font-normal text-ink-mute">({{ documentCount }})</span>
      </span>
      <!-- Composite invoice status chip (AB#21269 wave 14) - moved here from the removed pills strip. -->
      <span
        v-if="invoiceStatusLabel"
        data-testid="ledger-status-chip"
        :class="invoiceStatusClass ?? ''"
        :title="invoiceStatusTitle ?? undefined"
      >{{ invoiceStatusLabel }}</span>
    </button>

    <div v-if="expanded" class="border-t border-slate-200 px-4 py-3">
      <div
        v-for="section in sections"
        :key="section.key"
        class="mb-4 last:mb-0"
      >
        <p v-if="section.label" class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-mute">
          {{ section.label }}
        </p>

        <div class="overflow-x-auto">
          <table class="w-full min-w-[46rem] border-collapse text-left text-xs">
            <thead>
              <tr class="text-[11px] uppercase tracking-wide text-ink-mute">
                <th class="py-1 pr-3 font-medium">Document</th>
                <th class="py-1 pr-3 font-medium">Status</th>
                <th class="py-1 pr-3 font-medium">Created</th>
                <th class="py-1 pr-3 font-medium">Applies to</th>
                <th class="py-1 pr-3 text-right font-medium">Qty</th>
                <th
                  class="py-1 pr-3 text-right font-medium"
                  title="Document totals as issued by Stripe — tax included"
                >
                  Amount <span class="font-normal normal-case text-ink-mute">(incl. tax)</span>
                </th>
                <th class="py-1 pr-3 font-medium">PDF</th>
                <th class="py-1 pr-0 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="entry in section.entries" :key="entry.invoiceRow.id">
                <!-- Invoice row -->
                <tr class="border-t border-slate-100">
                  <td class="py-1.5 pr-3 font-mono text-ink">{{ entry.invoiceRow.number }}</td>
                  <td class="py-1.5 pr-3">
                    <span
                      v-if="entry.invoiceRow.status"
                      class="inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize leading-tight"
                      :class="statusPillClass(entry.invoiceRow.status)"
                    >{{ entry.invoiceRow.status }}</span>
                    <span v-else class="text-ink-mute">—</span>
                  </td>
                  <td class="py-1.5 pr-3 text-ink-soft">{{ formatInvoiceCreated(entry.invoiceRow.createdAt) }}</td>
                  <td class="py-1.5 pr-3 text-ink-mute">—</td>
                  <td class="py-1.5 pr-3 text-right tabular-nums text-ink-soft">
                    {{ entry.invoiceRow.invoicedQty ?? '—' }}
                  </td>
                  <td class="py-1.5 pr-3 text-right tabular-nums text-ink-soft">
                    {{ formatRowAmount(entry.invoiceRow) }}
                  </td>
                  <td class="py-1.5 pr-3">
                    <div class="flex items-center gap-2">
                      <button
                        v-if="entry.invoiceRow.pdfUrl"
                        type="button"
                        :disabled="isPdfOpening(entry.invoiceRow.id) || isPdfUnavailable(entry.invoiceRow.id)"
                        :title="isPdfUnavailable(entry.invoiceRow.id)
                          ? 'PDF not available yet — try again shortly'
                          : 'Invoice PDF — original as issued'"
                        :class="isPdfUnavailable(entry.invoiceRow.id)
                          ? 'cursor-not-allowed font-medium text-ink-mute'
                          : 'font-medium text-[#219f84] underline decoration-[#219f84]/40 underline-offset-2 hover:text-[#1a8068]'"
                        @click="handleOpenInvoicePdf(entry.invoiceRow.id, entry.raw)"
                      >
                        <span
                          v-if="isPdfOpening(entry.invoiceRow.id)"
                          class="mr-1 inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-current border-r-transparent align-[-1px]"
                        />Open</button>
                      <span v-else class="text-ink-mute">—</span>
                      <a
                        v-if="invoiceHostedUrl(entry.raw)"
                        :href="invoiceHostedUrl(entry.raw)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open the live payable invoice in Stripe"
                        class="text-[11px] font-medium text-ink-mute underline decoration-slate-300 underline-offset-2 hover:text-ink-soft"
                      >Stripe</a>
                    </div>
                  </td>
                  <td class="py-1.5 pr-0">
                    <div class="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title="Refresh this invoice's Stripe link (fetches a fresh, non-expired URL)"
                        :disabled="refreshingInvoiceId === entry.invoiceRow.invoiceId"
                        class="shrink-0 rounded p-0.5 text-[#219f84] hover:bg-[#219f84]/10 disabled:cursor-not-allowed disabled:opacity-60"
                        @click="emit('refresh', entry.raw)"
                      >
                        <span
                          v-if="refreshingInvoiceId === entry.invoiceRow.invoiceId"
                          class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[#219f84] border-r-transparent"
                        />
                        <RefreshCw v-else class="h-3 w-3" />
                      </button>
                      <button
                        v-if="canRemoveInvoice(entry.raw)"
                        type="button"
                        title="Remove this invoice from the order"
                        class="shrink-0 rounded p-0.5 text-red-600 hover:bg-red-50"
                        @click="emit('remove', entry.raw)"
                      >
                        <Trash2 class="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Credit-note rows, indented under their invoice -->
                <tr v-for="cnRow in entry.cnRows" :key="cnRow.id" class="border-t border-slate-50">
                  <td class="py-1.5 pr-3 pl-4 font-mono text-ink-soft">{{ cnRow.number ?? cnRow.id }}</td>
                  <td class="py-1.5 pr-3">
                    <!-- AB#21269: ONE merged pill carries the settlement (finance feedback — no
                         extra tags/columns). Pre-payment CNs look exactly as before; refund and
                         out-of-band CNs get their own color + word. -->
                    <span
                      data-testid="cn-settlement-pill"
                      class="inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium leading-tight"
                      :class="settlementPill(cnRow).class"
                      :title="settlementPill(cnRow).tooltip"
                    >
                      {{ settlementPill(cnRow).label }}
                    </span>
                  </td>
                  <td class="py-1.5 pr-3 text-ink-soft">{{ formatCnCreated(cnRow.createdAt) }}</td>
                  <td class="py-1.5 pr-3 font-mono text-ink-mute">{{ entry.invoiceRow.number }}</td>
                  <td class="py-1.5 pr-3 text-right tabular-nums text-ink-soft">
                    {{ cnRow.qty != null ? cnRow.qty : '—' }}
                  </td>
                  <td class="py-1.5 pr-3 text-right tabular-nums text-red-700">
                    {{ formatRowAmount(cnRow) }}
                  </td>
                  <td class="py-1.5 pr-3">
                    <button
                      v-if="hasCreditNotePdfAffordance(cnRow)"
                      type="button"
                      :disabled="isPdfOpening(cnRow.id) || isPdfUnavailable(cnRow.id)"
                      :title="isPdfUnavailable(cnRow.id)
                        ? 'PDF not available yet — try again shortly'
                        : 'Credit note PDF'"
                      :class="isPdfUnavailable(cnRow.id)
                        ? 'cursor-not-allowed font-medium text-ink-mute'
                        : 'font-medium text-[#219f84] underline decoration-[#219f84]/40 underline-offset-2 hover:text-[#1a8068]'"
                      @click="handleOpenCreditNotePdf(cnRow)"
                    >
                      <span
                        v-if="isPdfOpening(cnRow.id)"
                        class="mr-1 inline-block h-2.5 w-2.5 animate-spin rounded-full border-2 border-current border-r-transparent align-[-1px]"
                      />Open</button>
                    <span v-else class="text-ink-mute">—</span>
                  </td>
                  <td class="py-1.5 pr-0"></td>
                </tr>

                <!-- Lazy credit-note fetch: loading / error, inline + non-blocking -->
                <tr v-if="entry.loading" class="border-t border-slate-50">
                  <td colspan="8" class="py-1.5 pl-4 text-[11px] italic text-ink-mute">
                    Loading credit notes…
                  </td>
                </tr>
                <tr v-else-if="entry.error" class="border-t border-slate-50">
                  <td colspan="8" class="py-1.5 pl-4 text-[11px] italic text-red-500">
                    {{ entry.error }}
                    <button
                      type="button"
                      class="ml-1 not-italic underline hover:text-red-700"
                      @click="retryEligibility(entry.raw)"
                    >Retry</button>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="mt-2 flex flex-wrap items-center justify-end gap-x-4 border-t border-slate-200 pt-2 text-xs">
        <span class="font-semibold text-ink-soft">Net billed:</span>
        <span
          v-for="total in netBilledTotals"
          :key="String(total.currency)"
          data-testid="net-billed-total"
          class="tabular-nums text-right"
        >
          <span class="font-semibold text-ink">{{ formatNetTotal(total) }}</span>
          <span class="ml-1 font-normal text-ink-mute">incl. tax</span>
          <!-- AB#21269: pre-tax/tax split — only rendered when every summed row carries its own
               frozen pre-tax figure (hourly sweep backfills stragglers), never a guessed split. -->
          <span
            v-if="total.exclTax !== null && total.tax !== null"
            data-testid="net-billed-breakdown"
            class="ml-1 font-normal text-ink-mute"
          >
            (excl. tax {{ formatAmountValue(total.exclTax) }} · tax {{ formatAmountValue(total.tax) }})
          </span>
        </span>
        <span v-if="netBilledTotals.length === 0" class="text-ink-mute">—</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Billing documents ledger (AB#20717) — additive, collapsed-by-default breakdown of
 * every Stripe invoice + credit note on an order, rendered directly below the existing
 * invoice chips row in `PriceBreakdownModal.vue`.
 *
 * Refresh/remove are NOT reimplemented here: this component only emits `refresh` /
 * `remove` with the raw invoice row, and the parent wires those to its existing
 * `refreshInvoiceLink` / `onRemoveInvoice` handlers — a single source of truth for both
 * actions (and their permission checks, confirm dialogs, etc).
 */
import { computed, ref, watch } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { RefreshCw, Trash2 } from 'lucide-vue-next';
import { can } from '@/composables';
import {
  buildLedgerRows,
  netBilled,
  type BillingLedgerCreditNoteRow,
  type BillingLedgerInvoiceRow,
  type BillingLedgerRow,
  type NetBilledTotal,
  type ResolveInvoiceUrlFn,
} from '@/utils/billingDocuments';
import {
  fetchInvoiceCreditEligibility,
  isCreditableInvoiceRow,
  type CreditNoteEligibility,
} from '@/utils/stripeCreditNote';
import { resolveInvoiceDocumentUrl } from '@/utils/invoicePdf';
import { openCreditNotePdf, openInvoicePdf } from '@/utils/documentPdfUrl';

interface Props {
  orderId: number;
  invoices: Record<string, unknown>[];
  orderItems: Record<string, unknown>[];
  /** Parent's URL resolver (participates in its "refreshed link" override cache). Defaults to the plain ladder. */
  resolveUrl?: ResolveInvoiceUrlFn;
  /** Parent's `canOfferRemoveInvoiceFor` — mirrors today's remove-button visibility exactly. */
  canRemoveInvoice?: (inv: Record<string, unknown>) => boolean;
  refreshingInvoiceId?: string | number | null;
  /** AB#21269 wave 14: composite invoice status chip in the header (the pills strip that used to
   *  carry it was removed as a duplicate of this ledger). Absent = no chip. */
  invoiceStatusLabel?: string | null;
  invoiceStatusClass?: string | null;
  invoiceStatusTitle?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  resolveUrl: undefined,
  canRemoveInvoice: () => false,
  refreshingInvoiceId: null,
  invoiceStatusLabel: null,
  invoiceStatusClass: null,
  invoiceStatusTitle: null,
});

const emit = defineEmits<{
  (e: 'refresh', inv: Record<string, unknown>): void;
  (e: 'remove', inv: Record<string, unknown>): void;
}>();

const expanded = ref(false);
const eligibilityFetchStarted = ref(false);
const eligibilityByInvoiceId = ref<Record<string, CreditNoteEligibility | undefined>>({});
const eligibilityLoadingIds = ref<Record<string, boolean>>({});
const eligibilityErrorById = ref<Record<string, string>>({});

/**
 * Per-ledger-row-id (`entry.invoiceRow.id` / `cnRow.id`) PDF-open state (AB#20717 round 3 — "No
 * Stripe redirect from the PDF column"). `openInvoicePdf`/`openCreditNotePdf` never fall back to a
 * Stripe-hosted link anymore, so a `'unavailable'` result needs its own row-level affordance
 * instead of silently doing nothing.
 */
const pdfOpeningRowIds = ref<Record<string, boolean>>({});
const pdfUnavailableRowIds = ref<Record<string, boolean>>({});

function isPdfOpening(rowId: string): boolean {
  return !!pdfOpeningRowIds.value[rowId];
}

function isPdfUnavailable(rowId: string): boolean {
  return !!pdfUnavailableRowIds.value[rowId];
}

function clearPdfUnavailable(rowId: string): void {
  if (!(rowId in pdfUnavailableRowIds.value)) return;
  const next = { ...pdfUnavailableRowIds.value };
  delete next[rowId];
  pdfUnavailableRowIds.value = next;
}

// The ledger refreshing (parent passes a new `invoices` array — e.g. after a refresh/remove/
// optimistic credit-note append) invalidates any "not available yet" flags: the underlying
// document set has changed, so a stale disabled state shouldn't linger.
watch(
  () => props.invoices,
  () => {
    pdfUnavailableRowIds.value = {};
  },
);

async function handleOpenInvoicePdf(rowId: string, raw: Record<string, unknown>): Promise<void> {
  if (isPdfOpening(rowId) || isPdfUnavailable(rowId)) return;
  pdfOpeningRowIds.value = { ...pdfOpeningRowIds.value, [rowId]: true };
  try {
    const result = await openInvoicePdf(raw);
    pdfUnavailableRowIds.value = { ...pdfUnavailableRowIds.value, [rowId]: result === 'unavailable' };
  } finally {
    pdfOpeningRowIds.value = { ...pdfOpeningRowIds.value, [rowId]: false };
  }
}

async function handleOpenCreditNotePdf(cnRow: BillingLedgerCreditNoteRow): Promise<void> {
  if (isPdfOpening(cnRow.id) || isPdfUnavailable(cnRow.id)) return;
  pdfOpeningRowIds.value = { ...pdfOpeningRowIds.value, [cnRow.id]: true };
  try {
    const result = await openCreditNotePdf({ ...(cnRow.raw ?? {}), pdf: cnRow.pdfUrl });
    pdfUnavailableRowIds.value = { ...pdfUnavailableRowIds.value, [cnRow.id]: result === 'unavailable' };
  } finally {
    pdfOpeningRowIds.value = { ...pdfOpeningRowIds.value, [cnRow.id]: false };
  }
}

const resolveUrlFn = computed<ResolveInvoiceUrlFn>(
  () => props.resolveUrl ?? ((inv: Record<string, unknown>) => resolveInvoiceDocumentUrl(inv)),
);

const flatRows = computed<BillingLedgerRow[]>(() =>
  buildLedgerRows(props.invoices ?? [], eligibilityByInvoiceId.value, resolveUrlFn.value),
);

const documentCount = computed(() => flatRows.value.length);
const netBilledTotals = computed<NetBilledTotal[]>(() => netBilled(flatRows.value));

interface LedgerEntry {
  invoiceRow: BillingLedgerInvoiceRow;
  raw: Record<string, unknown>;
  cnRows: BillingLedgerCreditNoteRow[];
  loading: boolean;
  error: string;
}

interface LedgerSection {
  key: string;
  label: string;
  entries: LedgerEntry[];
}

const sections = computed<LedgerSection[]>(() => {
  const rows = flatRows.value;
  const invoiceRows = rows.filter((r): r is BillingLedgerInvoiceRow => r.kind === 'invoice');
  const cnRowsByParent = new Map<string, BillingLedgerCreditNoteRow[]>();
  for (const row of rows) {
    if (row.kind !== 'credit_note') continue;
    const list = cnRowsByParent.get(row.parentInvoiceId) ?? [];
    list.push(row);
    cnRowsByParent.set(row.parentInvoiceId, list);
  }

  const showGroups = (props.orderItems?.length ?? 0) > 1;
  const sectionMap = new Map<string, LedgerSection>();

  for (const invRow of invoiceRows) {
    const oiId = invRow.raw?.order_item_id;
    const key = showGroups ? String(oiId ?? 'none') : 'all';
    if (!sectionMap.has(key)) {
      let label = '';
      if (showGroups) {
        const item = (props.orderItems ?? []).find(
          (it) => String((it as Record<string, unknown>)?.id) === key,
        ) as Record<string, unknown> | undefined;
        label = item
          ? `Item ${String(item.item_no ?? item.id ?? key)}`
          : 'Other';
      }
      sectionMap.set(key, { key, label, entries: [] });
    }
    const invIdKey = invRow.invoiceId !== null && invRow.invoiceId !== undefined ? String(invRow.invoiceId) : '';
    sectionMap.get(key)!.entries.push({
      invoiceRow: invRow,
      raw: invRow.raw,
      cnRows: cnRowsByParent.get(invRow.id) ?? [],
      loading: !!eligibilityLoadingIds.value[invIdKey],
      error: eligibilityErrorById.value[invIdKey] ?? '',
    });
  }

  return Array.from(sectionMap.values());
});

async function fetchEligibilityForInvoice(inv: Record<string, unknown>): Promise<void> {
  // AB#23112: the eligibility endpoint needs `create-credit-note`. Without it, skip the call (no error
  // chip); existing credit notes still render from the `credit_notes[]` embedded in the invoice rows.
  if (!can('create-credit-note')) return;
  if (!isCreditableInvoiceRow(inv)) return;
  const id = String((inv as Record<string, unknown>).id);
  eligibilityLoadingIds.value = { ...eligibilityLoadingIds.value, [id]: true };
  eligibilityErrorById.value = { ...eligibilityErrorById.value, [id]: '' };
  try {
    const result = await fetchInvoiceCreditEligibility(props.orderId, Number(id));
    eligibilityByInvoiceId.value = { ...eligibilityByInvoiceId.value, [id]: result };
  } catch {
    // fetchInvoiceCreditEligibility already routes the error through handleResponseException;
    // this inline message just keeps the ledger row itself informative without a second toast.
    eligibilityErrorById.value = { ...eligibilityErrorById.value, [id]: 'Could not load credit notes.' };
  } finally {
    eligibilityLoadingIds.value = { ...eligibilityLoadingIds.value, [id]: false };
  }
}

async function loadEligibilityOnce(): Promise<void> {
  if (eligibilityFetchStarted.value) return;
  eligibilityFetchStarted.value = true;
  const creditable = (props.invoices ?? []).filter((inv) => isCreditableInvoiceRow(inv));
  await Promise.allSettled(creditable.map((inv) => fetchEligibilityForInvoice(inv)));
}

function retryEligibility(inv: Record<string, unknown>): void {
  void fetchEligibilityForInvoice(inv);
}

/**
 * Live refresh (AB#20717 round 2 fix #4): the parent calls this via template ref right after the
 * wizard optimistically appends a newly created credit note to `props.invoices`, so the ledger's
 * qty/pdf-fallback enrichment for that invoice re-fetches instead of staying pinned to whatever
 * eligibility snapshot was cached before the credit note existed. Deliberately NOT a full remount
 * (no `:key` bump) so the ledger's `expanded` state survives the refresh.
 *
 * Re-fetches unconditionally (not gated on `expanded.value`/`eligibilityFetchStarted`) — the ledger's
 * initial load is a one-shot (`loadEligibilityOnce`), so a collapsed ledger that had already been
 * expanded once wouldn't otherwise pick up this invalidation on its next expand.
 */
function invalidateInvoiceEligibility(invoiceId: number | string): void {
  const id = String(invoiceId);
  if (id in eligibilityByInvoiceId.value) {
    const next = { ...eligibilityByInvoiceId.value };
    delete next[id];
    eligibilityByInvoiceId.value = next;
  }
  // A re-fetch may surface a credit note whose PDF wasn't ready a moment ago — drop any stale
  // "not available yet" flag on this invoice's own row so it re-renders as a normal link.
  clearPdfUnavailable(`inv-${id}`);
  const inv = (props.invoices ?? []).find((i) => String((i as Record<string, unknown>)?.id ?? '') === id);
  if (inv) void fetchEligibilityForInvoice(inv);
}

/** `hosted_invoice_url` straight off the raw invoice row — the explicit "open in Stripe" link is
 *  intentionally NOT run through the S3/refreshed-link ladder; it always targets Stripe's own page. */
function invoiceHostedUrl(inv: Record<string, unknown> | null | undefined): string | null {
  const url = (inv as Record<string, unknown> | null | undefined)?.hosted_invoice_url;
  return typeof url === 'string' && url.trim() !== '' ? url : null;
}

function hasCreditNotePdfAffordance(row: BillingLedgerCreditNoteRow): boolean {
  if (row.pdfUrl) return true;
  const key = (row.raw as Record<string, unknown> | undefined)?.credit_note_pdf_s3_key;
  return typeof key === 'string' && key.trim() !== '';
}

function toggleExpanded(): void {
  expanded.value = !expanded.value;
  if (expanded.value) void loadEligibilityOnce();
}

function statusPillClass(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'paid') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (s === 'open' || s === 'draft') return 'border-amber-200 bg-amber-50 text-amber-900';
  if (s === 'void' || s === 'uncollectible') return 'border-red-200 bg-red-50 text-red-800';
  return 'border-slate-200 bg-surface-2 text-ink-soft';
}

function formatInvoiceCreated(createdAt: string | null): string {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

/** Stripe `created` is unix seconds. */
function formatCnCreated(unixSeconds: number | null): string {
  if (!unixSeconds || !Number.isFinite(unixSeconds)) return '—';
  try {
    return new Date(unixSeconds * 1000).toLocaleDateString();
  } catch {
    return '—';
  }
}

function formatAmountValue(amount: number): string {
  return Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRowAmount(row: BillingLedgerRow): string {
  if (row.amount === null || row.amount === undefined) return '—';
  const sign = row.amount < 0 ? '−' : '';
  const currency = row.currency ? ` ${row.currency}` : '';
  return `${sign}${formatAmountValue(row.amount)}${currency}`;
}

/**
 * The credit-note row's single status pill, settlement-aware (AB#21269). Pre-payment (and
 * unknown-settlement) rows render exactly the pill this table always had.
 */
function settlementPill(cnRow: BillingLedgerCreditNoteRow): { label: string; class: string; tooltip: string | undefined } {
  if (cnRow.settlement === 'card_refund') {
    return {
      label: 'Credit Note · Refunded',
      class: 'border-indigo-200 bg-indigo-50 text-indigo-800',
      tooltip: 'Created after the invoice was paid — this amount was refunded to the customer’s card.',
    };
  }
  // AB#22842 — the persisted settlement choice distinguishes the two out-of-band flavors.
  if (cnRow.settlement === 'bank_refund') {
    return {
      label: 'Credit Note · Bank refund',
      class: 'border-amber-200 bg-amber-50 text-amber-800',
      tooltip: 'Recorded as credited outside of Stripe: finance transfers the money by bank.',
    };
  }
  if (cnRow.settlement === 'no_refund') {
    return {
      label: 'Credit Note · No refund',
      class: 'border-slate-200 bg-surface-2 text-ink-soft',
      tooltip: 'Recorded as credited outside of Stripe. No money moves.',
    };
  }
  if (cnRow.settlement === 'out_of_band') {
    return {
      label: 'Credit Note · Out-of-band',
      class: 'border-amber-200 bg-amber-50 text-amber-800',
      tooltip: 'Created after the invoice was paid — recorded for manual settlement by finance.',
    };
  }
  return {
    label: 'Credit Note',
    class: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    tooltip: cnRow.settlement === 'pre_payment'
      ? 'Created before payment — it reduced the invoice’s amount due.'
      : undefined,
  };
}

function formatNetTotal(total: NetBilledTotal): string {
  const sign = total.net < 0 ? '−' : '';
  const currency = total.currency ? ` ${total.currency}` : '';
  return `${sign}${formatAmountValue(total.net)}${currency}`;
}

defineExpose({
  toggleExpanded,
  sections,
  documentCount,
  netBilledTotals,
  invalidateInvoiceEligibility,
});
</script>
